import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Card,
  Table,
  Badge,
  Select,
  TextField,
} from '@radix-ui/themes';
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../services/api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  classifyBloodPressure,
  getBPColor,
  getLatestBloodPressure,
  getBPSortPriority,
} from '../../utils/bloodPressureValidation';
import { matchesPatientSearch } from '../../utils/patientSearch';

interface UserOption {
  id: string;
  email: string;
  name?: string | null;
}

interface ViewAsPatient {
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  last_successful_data_collection?: string | null;
  last_attempt_to_collect_data?: string | null;
  care_plan_definitions?: any[];
  most_recent_surveys?: any[];
  problems?: any[];
  medications?: any[];
  organization_name?: string;
  [key: string]: any;
}

type SortField = 'lastName' | 'firstName' | 'latestAnswer' | 'lastSent' | 'bloodPressure' | 'bpDate';
type SortDirection = 'asc' | 'desc';

const needsContinueOnboarding = (patient: ViewAsPatient): boolean => {
  return !patient.care_plan_definitions || patient.care_plan_definitions.length === 0;
};

const getOnboardingStepLabel = (patient: ViewAsPatient): string | null => {
  const hasCarePlans = patient.care_plan_definitions && patient.care_plan_definitions.length > 0;
  if (hasCarePlans) return null;

  const hasProblems = patient.problems && patient.problems.length > 0;
  const hasMedications = patient.medications && patient.medications.length > 0;
  if (hasProblems || hasMedications) {
    return 'Approve Care Plan';
  }
  return 'Document Upload';
};

const normalizeUsers = (data: any): UserOption[] => {
  const records = data?.records || data?.users || data || [];
  if (!Array.isArray(records)) return [];
  return records
    .filter((r: any) => !r.archived && !r.is_archived && !r.archived_at)
    .map((record: any) => ({
      id: String(record.id ?? record.user_id ?? record.userId ?? record.sub),
      email: record.email ?? 'unknown',
      name: record.name ?? record.full_name ?? null,
    }));
};


const AdminViewAsPage: React.FC = () => {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [patients, setPatients] = useState<ViewAsPatient[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [sortField, setSortField] = useState<SortField>('lastName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  // PHA-527: org-state from OrganizationContext.
  const { orgId: ctxOrgId, loading: orgLoading } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;
  const sessionLoaded = !orgLoading;

  useEffect(() => {
    if (!sessionLoaded) return;
    const loadUsers = async () => {
      setLoadingUsers(true);
      try {
        const params: { organization_id?: string } = {};
        if (sessionOrgId && sessionOrgId !== 'ALL') {
          params.organization_id = sessionOrgId;
        }
        const data = await adminApi.getUsers(params);
        setUsers(normalizeUsers(data));
      } catch (err: any) {
        setError('Failed to load users: ' + (err.message || 'Unknown error'));
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [sessionLoaded, sessionOrgId]);

  // Load patients when user is selected
  const loadPatients = useCallback(async (userId: string) => {
    if (!userId) {
      setPatients([]);
      return;
    }
    setLoadingPatients(true);
    setError(null);
    try {
      const data = await adminApi.viewAsPatients(userId);
      const records = data?.records || data || [];
      setPatients(Array.isArray(records) ? records : []);
    } catch (err: any) {
      setError('Failed to load patients: ' + (err.message || 'Unknown error'));
      setPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  }, []);

  const handleUserChange = useCallback((userId: string) => {
    setSelectedUserId(userId);
    setSearchQuery('');
    loadPatients(userId);
  }, [loadPatients]);

  // Sort toggle
  const handleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return field;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  // Sortable header component
  const SortableHeader: React.FC<{
    field: SortField;
    label: string;
    style?: React.CSSProperties;
  }> = ({ field, label, style }) => (
    <Table.ColumnHeaderCell
      style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', ...style }}
      onClick={() => handleSort(field)}
    >
      <Flex align="center" gap="1">
        <Text size="1">{label}</Text>
        {sortField === field && (
          sortDirection === 'asc' ? <ChevronUpIcon width="12" height="12" /> : <ChevronDownIcon width="12" height="12" />
        )}
      </Flex>
    </Table.ColumnHeaderCell>
  );

  // Filter and sort patients
  const sortedPatients = useMemo(() => {
    let filtered = patients;
    if (debouncedSearchQuery) {
      filtered = patients.filter(p => matchesPatientSearch(p, debouncedSearchQuery));
    }

    const sorted = [...filtered].sort((a, b) => {
      let compareValue = 0;
      switch (sortField) {
        case 'lastName':
          compareValue = (a.last_name || '').localeCompare(b.last_name || '');
          break;
        case 'firstName':
          compareValue = (a.first_name || '').localeCompare(b.first_name || '');
          break;
        case 'latestAnswer': {
          const aDate = a.last_successful_data_collection ? new Date(a.last_successful_data_collection).getTime() : 0;
          const bDate = b.last_successful_data_collection ? new Date(b.last_successful_data_collection).getTime() : 0;
          compareValue = aDate - bDate;
          break;
        }
        case 'lastSent': {
          const aDate = a.last_attempt_to_collect_data ? new Date(a.last_attempt_to_collect_data).getTime() : 0;
          const bDate = b.last_attempt_to_collect_data ? new Date(b.last_attempt_to_collect_data).getTime() : 0;
          compareValue = aDate - bDate;
          break;
        }
        case 'bloodPressure': {
          const aPriority = getBPSortPriority(a);
          const bPriority = getBPSortPriority(b);
          compareValue = bPriority - aPriority;
          break;
        }
        case 'bpDate': {
          const aBp = getLatestBloodPressure(a);
          const bBp = getLatestBloodPressure(b);
          const aDate = aBp?.date ? new Date(aBp.date).getTime() : 0;
          const bDate = bBp?.date ? new Date(bBp.date).getTime() : 0;
          compareValue = aDate - bDate;
          break;
        }
      }
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });

    return sorted;
  }, [patients, debouncedSearchQuery, sortField, sortDirection]);

  const selectedUser = users.find(u => u.id === selectedUserId);

  return (
    <AdminLayout
      title="View As User"
      subtitle="See the patient list as a specific user would see it (read-only)"
    >
      {/* User selector */}
      <Box className="mb-4">
        <Flex align="center" gap="3">
          <Text size="2" weight="medium">Select user:</Text>
          <Select.Root
            value={selectedUserId}
            onValueChange={handleUserChange}
            disabled={loadingUsers}
          >
            <Select.Trigger
              placeholder={loadingUsers ? 'Loading users...' : 'Choose a user...'}
              style={{ minWidth: '350px' }}
            />
            <Select.Content>
              {users.map(user => (
                <Select.Item key={user.id} value={user.id}>
                  {user.name ? `${user.name} (${user.email})` : user.email}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          {selectedUser && (
            <Badge color="blue" variant="soft">
              {sortedPatients.length} patient{sortedPatients.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </Flex>
      </Box>

      {error && (
        <Box className="mb-4 p-3 bg-red-50 border border-red-200 rounded">
          <Text size="2" color="red">{error}</Text>
        </Box>
      )}

      {/* Search */}
      {selectedUserId && patients.length > 0 && (
        <Box className="mb-4">
          <TextField.Root size="2" style={{ maxWidth: '400px' }}>
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
            <TextField.Input
              placeholder="Search by name or date of birth..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </TextField.Root>
          {debouncedSearchQuery && (
            <Text size="1" className="text-slate-500 mt-1">
              Showing {sortedPatients.length} of {patients.length} patients
            </Text>
          )}
        </Box>
      )}

      {/* Loading state */}
      {loadingPatients && (
        <Box className="p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <Text size="3" className="text-slate-600">Loading patients...</Text>
        </Box>
      )}

      {/* Empty state */}
      {!loadingPatients && selectedUserId && patients.length === 0 && !error && (
        <Box className="p-6 text-center">
          <Text size="3" className="text-slate-500">No patients found for this user.</Text>
        </Box>
      )}

      {/* No user selected */}
      {!selectedUserId && !loadingUsers && (
        <Box className="p-6 text-center">
          <Text size="3" className="text-slate-500">Select a user to view their patient list.</Text>
        </Box>
      )}

      {/* Patient table (read-only) */}
      {!loadingPatients && sortedPatients.length > 0 && (
        <Card className="shadow-sm overflow-x-auto">
          <Table.Root size="1" variant="surface">
            <Table.Header>
              <Table.Row className="bg-slate-50">
                <Table.ColumnHeaderCell style={{ width: '110px', verticalAlign: 'middle' }}>MRN</Table.ColumnHeaderCell>
                <SortableHeader field="firstName" label="First" />
                <SortableHeader field="lastName" label="Last" />
                <Table.ColumnHeaderCell style={{ verticalAlign: 'middle' }}>Clinic</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell style={{ width: '85px', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>DOB</Table.ColumnHeaderCell>
                <SortableHeader field="latestAnswer" label="Answer" style={{ width: '85px', whiteSpace: 'nowrap' }} />
                <SortableHeader field="lastSent" label="Last Sent" style={{ width: '85px', whiteSpace: 'nowrap' }} />
                <Table.ColumnHeaderCell style={{ verticalAlign: 'middle' }}>Status</Table.ColumnHeaderCell>
                <SortableHeader field="bloodPressure" label="BP" />
                <SortableHeader field="bpDate" label="BP Date" style={{ width: '100px', whiteSpace: 'nowrap' }} />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedPatients.map((patient) => (
                <Table.Row key={patient.patient_id} className="hover:bg-slate-50">
                  <Table.Cell>
                    <Text size="1" weight="medium">{patient.patient_id}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" weight="medium" className="text-slate-800">
                      {patient.first_name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="2" weight="medium" className="text-slate-800">
                      {patient.last_name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Text size="1" className="text-slate-600">
                      {patient.organization_name || 'N/A'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {(() => {
                        const [year, month, day] = patient.date_of_birth.split('-').map(Number);
                        const date = new Date(year, month - 1, day);
                        return format(date, 'MMM dd, yy');
                      })()}
                    </Text>
                  </Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {patient.last_successful_data_collection
                        ? format(new Date(patient.last_successful_data_collection), 'MMM dd, yy')
                        : 'N/A'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {patient.last_attempt_to_collect_data
                        ? format(new Date(patient.last_attempt_to_collect_data), 'MMM dd, yy')
                        : 'N/A'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>
                    {(() => {
                      const stepLabel = getOnboardingStepLabel(patient);
                      return stepLabel ? (
                        <Badge size="1" color="orange" variant="soft">
                          {stepLabel}
                        </Badge>
                      ) : null;
                    })()}
                  </Table.Cell>
                  <Table.Cell>
                    {(() => {
                      const bp = getLatestBloodPressure(patient);
                      if (!bp?.systolic && !bp?.diastolic) {
                        return <Text size="1" className="text-slate-400">N/A</Text>;
                      }
                      const category = classifyBloodPressure(bp.systolic, bp.diastolic);
                      const color = getBPColor(category);
                      return (
                        <Badge size="1" color={color} variant="soft">
                          {bp.systolic || '?'}/{bp.diastolic || '?'}
                        </Badge>
                      );
                    })()}
                  </Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    {(() => {
                      const bp = getLatestBloodPressure(patient);
                      if (!bp?.date) {
                        return <Text size="1" className="text-slate-400">N/A</Text>;
                      }
                      return (
                        <Text size="1" className="text-slate-600">
                          {format(new Date(bp.date), 'MMM dd, yy')}
                        </Text>
                      );
                    })()}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminViewAsPage;
